import React, { useCallback, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import SavedDrafts from './SavedDrafts';
import DraftBoard from './DraftBoard';
import { useDraft } from '../contexts/DraftContext';

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const DraftsPageContent = () => {
  const location = useLocation();
  const isEmbedView = location.pathname === '/embed';
  const {
    scenariosFirstPick,
    scenariosSecondPick,
    teamName,
    tournament,
    stage,
    draftDate,
    pickMode,
    updateScenario,
    addScenario,
    setTeamName,
    setTournament,
    setDraftDate,
    setPulledMatchHeroes,
    importDraftData
  } = useDraft();

  // Embed view: persist current draft to sessionStorage on every change so
  // closing/reopening the overlay (parent host writes pendingDraftLoad back
  // from this snapshot) preserves user edits within the session.
  useEffect(() => {
    if (!isEmbedView) return;
    try {
      const snapshot = {
        id: `embed_snapshot_${Date.now()}`,
        teamName, tournament, stage,
        date: draftDate,
        layoutVersion: '4.0',
        scenariosFirstPick,
        scenariosSecondPick
      };
      sessionStorage.setItem('embedDraftSnapshot', JSON.stringify(snapshot));
    } catch (e) {
      /* ignore quota errors */
    }
  }, [isEmbedView, scenariosFirstPick, scenariosSecondPick, teamName, tournament, stage, draftDate]);

  // Track pending match data that needs to be added to a new scenario
  const [pendingMatchData, setPendingMatchData] = useState(null);

  // Track color index for match pulls
  const [matchPullColorIndex, setMatchPullColorIndex] = useState(0);

  // Check for pending draft load on mount (after login).
  // When ?loadKey=<key> is in the URL (multi-iframe gallery), read from that
  // specific sessionStorage key instead of the shared pendingDraftLoad slot.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const loadKey = params.get('loadKey');
    const storageKey = loadKey || 'pendingDraftLoad';
    const pendingDraft = sessionStorage.getItem(storageKey);
    if (pendingDraft) {
      try {
        const draftData = JSON.parse(pendingDraft);
        importDraftData(draftData);
        if (!loadKey) sessionStorage.removeItem('pendingDraftLoad');
        console.log('✅ Loaded pending draft after login');
      } catch (error) {
        console.error('❌ Error loading pending draft:', error);
        if (!loadKey) sessionStorage.removeItem('pendingDraftLoad');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount - importDraftData is stable from context

  // Effect to handle pending match data after new scenario is added
  useEffect(() => {
    if (pendingMatchData) {
      if (pendingMatchData.pickMode === 'both') {
        // Apply to both pick modes
        if (scenariosFirstPick.length > 0 && scenariosSecondPick.length > 0) {
          const lastFirst = scenariosFirstPick[scenariosFirstPick.length - 1];
          const lastSecond = scenariosSecondPick[scenariosSecondPick.length - 1];

          const updateData = {
            title: pendingMatchData.title || 'Match Pull',
            picks: pendingMatchData.picks,
            fromMatchPull: true,
            matchColorIndex: pendingMatchData.colorIndex,
            swapSides: false  // Always show as 1st pick view (not swapped)
          };

          if (pendingMatchData.practiceMatchups) {
            updateData.practiceMatchups = pendingMatchData.practiceMatchups;
          }

          updateScenario('first', lastFirst.id, updateData);
          updateScenario('second', lastSecond.id, updateData);
          setPendingMatchData(null);
        }
      } else {
        // Original single-mode logic
        const scenarios = pendingMatchData.pickMode === 'first' ? scenariosFirstPick : scenariosSecondPick;
        if (scenarios.length > 0) {
          const lastScenario = scenarios[scenarios.length - 1];
          const updateData = {
            title: pendingMatchData.title || lastScenario.title,
            picks: pendingMatchData.picks,
            fromMatchPull: true,
            matchColorIndex: pendingMatchData.colorIndex
          };

          if (pendingMatchData.practiceMatchups) {
            updateData.practiceMatchups = pendingMatchData.practiceMatchups;
          }

          updateScenario(pendingMatchData.pickMode, lastScenario.id, updateData);
          setPendingMatchData(null);
        }
      }
    }
  }, [scenariosFirstPick, scenariosSecondPick, pendingMatchData, updateScenario]);

  const handleMatchDataPulled = useCallback((matchData) => {
    try {
      // Extract hero IDs AND positions from the pulled match data for highlighting
      // Store as array of {heroId, position, matchId} objects
      const matchId = matchData.title || `Match ${Date.now()}`;
      const heroData = matchData.picks
        ?.map((pick, index) => ({
          heroId: pick?.hero?.id || null,
          position: index + 1, // Position is 1-indexed (1-24)
          matchId: matchId // Track which match this is from
        }))
        .filter(item => item.heroId) || []; // Filter out null/undefined heroes

      console.log('🎯 Match pulled! Hero data:', heroData);
      console.log('📊 Match data:', matchData);

      // APPEND the new match data to existing pulls (don't replace!)
      // This allows stacking badges from multiple matches
      setPulledMatchHeroes(prev => [...prev, ...heroData]);
      console.log('✅ Appended hero data to context (total matches pulled)');

      // Add to BOTH 1st pick and 2nd pick modes
      // Store the match data for both pick modes to be applied after scenarios are created
      setPendingMatchData({
        ...matchData,
        pickMode: 'both', // Special flag to indicate both modes
        colorIndex: matchPullColorIndex,  // Assign current color index
        practiceMatchups: matchData.practiceMatchups || null  // Include laning matchups if available
      });

      // Increment color index for next match pull
      setMatchPullColorIndex(prev => prev + 1);

      // Add scenario to both pick modes
      addScenario('first');
      addScenario('second');

      // Update the context fields for consistency
      if (matchData.teamName) setTeamName(matchData.teamName);
      if (matchData.tournament) setTournament(matchData.tournament);
      if (matchData.date) setDraftDate(matchData.date);

    } catch (error) {
      console.error('Error handling pulled match data:', error);
    }
  }, [addScenario, setTeamName, setTournament, setDraftDate, matchPullColorIndex, setPulledMatchHeroes]);

  return (
    <ContentContainer>
      {!isEmbedView && <SavedDrafts onMatchDataPulled={handleMatchDataPulled} />}
      <DraftBoard isEmbedView={isEmbedView} />
    </ContentContainer>
  );
};

export default DraftsPageContent;